import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';
import { formatTime, type TimeFormatPreference } from '@doccareplus/shared/lib/date';
import { FieldLabelMark } from './Input';
import { Modal } from './Modal';

export interface TimeFieldProps {
  /** Selected time as 24-hour `HH:mm`, or null when empty. Matches native `<input type="time">` value. */
  value: string | null;
  onChange: (value: string) => void;
  /** How the trigger and dialog are displayed. Values reported to `onChange` are always 24h `HH:mm`. */
  timeFormat?: TimeFormatPreference;
  /** Minutes between marks on the minute clock face (default 5). Also drives the typed-input stepper. */
  minuteStep?: number;
  /** Earliest selectable time, `HH:mm`. Earlier hours/minutes are disabled. */
  minTime?: string;
  /** Latest selectable time, `HH:mm`. Later hours/minutes are disabled. */
  maxTime?: string;
  label?: string;
  error?: string;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  requiredMark?: boolean;
  optional?: boolean;
  /** Kept for API compatibility with the previous anchored-popover version; a centered dialog ignores it. */
  placement?: 'bottom' | 'top';
  className?: string;
  triggerAriaLabel?: string;
  /** Optional controlled dialog state, used when another control should open the picker directly. */
  dialogOpen?: boolean;
  onDialogOpenChange?: (open: boolean) => void;
  /** Keep the dialog behavior while omitting this component's own time-field trigger. */
  hideTrigger?: boolean;
}

type ClockMode = 'hour' | 'minute';
type InputMode = 'clock' | 'keyboard';
type Meridiem = 'AM' | 'PM';

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/** A Date on a fixed day carrying only the given wall-clock time, for locale-aware formatting. */
function timeToDate(hhmm: string): Date {
  const [h, m] = hhmm.split(':').map(Number);
  return new Date(2000, 0, 1, h || 0, m || 0, 0);
}

function parseHHmm(hhmm: string): { hour: number; minute: number } {
  const [h, m] = hhmm.split(':').map(Number);
  return { hour: h || 0, minute: m || 0 };
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Position (percentage of container) for a clock-face mark at `angleDeg` (0 = top, clockwise) and `radiusPct`. */
function pointAt(angleDeg: number, radiusPct: number): { left: string; top: string } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    left: `${50 + radiusPct * Math.sin(rad)}%`,
    top: `${50 - radiusPct * Math.cos(rad)}%`,
  };
}

interface ClockMark {
  key: string;
  label: string;
  value: number;
  angleDeg: number;
  radiusPct: number;
  selected: boolean;
  disabled: boolean;
}

/**
 * The analog clock face: a ring of hour or minute marks around a center hand. Supports click-to-select
 * and drag-to-select (pointer events computing the nearest mark from the angle to the pointer).
 * Real angle-from-pointer geometry only works with real layout (a real browser); in jsdom
 * `getBoundingClientRect` returns zeros, so drag selection is not meaningfully unit-testable — click
 * selection (which needs no geometry) is what the test suite exercises.
 *
 * `onPreview` fires continuously while dragging (pointerdown/pointermove) to move the hand live,
 * without side effects like the hour→minute mode advance. `onCommit` fires once the interaction ends
 * (pointerup, or a plain click) and is where that advance happens — otherwise just touching down to
 * start a drag on the hour ring would instantly flip to minute mode before the drag even began.
 */
function ClockFace({
  marks,
  handAngleDeg,
  handRadiusPct,
  onPreview,
  onCommit,
  ariaLabel,
}: {
  marks: ClockMark[];
  handAngleDeg: number;
  handRadiusPct: number;
  onPreview: (value: number) => void;
  onCommit: (value: number) => void;
  ariaLabel: string;
}) {
  const faceRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const nearestMarkFromPoint = (clientX: number, clientY: number): ClockMark | null => {
    const rect = faceRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    // atan2 with (dx, -dy) so 0deg = top, clockwise positive — matches pointAt's convention.
    let angle = (Math.atan2(dx, -dy) * 180) / Math.PI;
    if (angle < 0) angle += 360;
    let best: ClockMark | null = null;
    let bestDelta = Infinity;
    for (const mark of marks) {
      if (mark.disabled) continue;
      const delta = Math.min(Math.abs(mark.angleDeg - angle), 360 - Math.abs(mark.angleDeg - angle));
      if (delta < bestDelta) {
        bestDelta = delta;
        best = mark;
      }
    }
    return best;
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    faceRef.current?.setPointerCapture(event.pointerId);
    const mark = nearestMarkFromPoint(event.clientX, event.clientY);
    if (mark) onPreview(mark.value);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const mark = nearestMarkFromPoint(event.clientX, event.clientY);
    if (mark) onPreview(mark.value);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    if (faceRef.current?.hasPointerCapture(event.pointerId)) {
      faceRef.current.releasePointerCapture(event.pointerId);
    }
    const mark = nearestMarkFromPoint(event.clientX, event.clientY);
    if (mark) onCommit(mark.value);
  };

  const handPoint = pointAt(handAngleDeg, handRadiusPct);

  return (
    <div
      ref={faceRef}
      role="group"
      aria-label={ariaLabel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      className="relative mx-auto aspect-square w-[248px] select-none rounded-full bg-[var(--surface-2)] touch-none"
    >
      {/* Hand */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[2px] origin-left rounded-full bg-[var(--primary)] transition-transform duration-150 ease-out"
        style={{ width: `${handRadiusPct}%`, transform: `rotate(${handAngleDeg - 90}deg)` }}
      />
      {/* Center dot */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--primary)]"
      />
      {/* Hand tip dot */}
      <div
        aria-hidden="true"
        className="absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--primary)] transition-[left,top] duration-150 ease-out"
        style={handPoint}
      />

      {marks.map((mark) => {
        const { left, top } = pointAt(mark.angleDeg, mark.radiusPct);
        return (
          <button
            key={mark.key}
            type="button"
            disabled={mark.disabled}
            aria-pressed={mark.selected}
            onClick={() => onCommit(mark.value)}
            style={{ left, top }}
            className={cn(
              'absolute flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full',
              'text-[13px] font-semibold tabular-nums transition-colors',
              'text-[var(--text-2)] hover:bg-[var(--surface-3)]',
              'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
              mark.selected && 'bg-[var(--primary)] text-white hover:bg-[var(--primary)]'
            )}
          >
            {mark.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * TimeField — pure presentational time picker. The trigger opens an Android Material-style
 * TimePickerDialog: a digital HH:MM header (plus AM/PM for 12h), an analog clock face for picking
 * the hour then the minute (click or drag), a toggle into a typed keyboard-input mode, and
 * Cancel/OK footer actions. Only OK commits the draft via `onChange`; Cancel discards it.
 *
 * Controlled: reports the chosen time as 24-hour `HH:mm` via `onChange` regardless of how it is
 * displayed. `timeFormat` (default 12h) drives the label rendering through the shared `formatTime`
 * seam. Owns no business rules; colours/spacing derive from design-system tokens.
 */
export function TimeField({
  value,
  onChange,
  timeFormat = '12h',
  minuteStep = 5,
  minTime,
  maxTime,
  label,
  error,
  placeholder,
  id,
  disabled,
  requiredMark,
  optional,
  className,
  triggerAriaLabel,
  dialogOpen,
  onDialogOpenChange,
  hideTrigger = false,
}: TimeFieldProps) {
  const reactId = useId();
  const fieldId = id ?? `time-field-${reactId}`;

  const [internalOpen, setInternalOpen] = useState(false);
  const open = dialogOpen ?? internalOpen;
  const [mode, setMode] = useState<ClockMode>('hour');
  const [inputMode, setInputMode] = useState<InputMode>('clock');
  // The in-progress selection while the dialog is open. Seeded from `value` (or, when empty, the
  // time nearest "now") each time the dialog opens; only committed to `onChange` when OK is pressed.
  const [draft, setDraft] = useState<string>(value ?? '00:00');

  const step = minuteStep > 0 ? minuteStep : 5;

  const minuteMarkCount = useMemo(() => Math.max(1, Math.min(60, Math.round(60 / step))), [step]);
  const minuteValues = useMemo(
    () => Array.from({ length: minuteMarkCount }, (_, i) => Math.round((i * 60) / minuteMarkCount) % 60),
    [minuteMarkCount]
  );

  // Nearest-to-now full HH:mm, used only to seed the draft when the field opens empty.
  const nearestToNow = useMemo(() => {
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    let best = '00:00';
    let bestDelta = Infinity;
    for (const m of minuteValues) {
      for (let h = 0; h < 24; h++) {
        const mins = h * 60 + m;
        const delta = Math.abs(mins - nowMins);
        if (delta < bestDelta) {
          bestDelta = delta;
          best = `${pad(h)}:${pad(m)}`;
        }
      }
    }
    return best;
  }, [minuteValues]);

  const setDialogOpen = (nextOpen: boolean) => {
    if (dialogOpen === undefined) setInternalOpen(nextOpen);
    onDialogOpenChange?.(nextOpen);
  };

  const prepareDraft = () => {
    setDraft(value ?? nearestToNow);
    setMode('hour');
    setInputMode('clock');
  };

  const openDialog = () => {
    prepareDraft();
    setDialogOpen(true);
  };

  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) prepareDraft();
    wasOpen.current = open;
  }, [open]);

  const cancel = () => setDialogOpen(false);

  const commit = () => {
    onChange(draft);
    setDialogOpen(false);
  };

  const { hour: draftHour, minute: draftMinute } = parseHHmm(draft);
  const meridiem: Meridiem = draftHour < 12 ? 'AM' : 'PM';

  const isMinuteOutOfRange = (hour: number, minute: number) => {
    const hhmm = `${pad(hour)}:${pad(minute)}`;
    return (minTime != null && hhmm < minTime) || (maxTime != null && hhmm > maxTime);
  };
  // An hour is disabled only when every minute mark within it is out of range.
  const isHourOutOfRange = (hour: number) => minuteValues.every((m) => isMinuteOutOfRange(hour, m));
  const isMeridiemOutOfRange = (mer: Meridiem) => {
    const hours = mer === 'AM' ? [...Array(12).keys()] : [...Array(12).keys()].map((h) => h + 12);
    return hours.every((h) => isHourOutOfRange(h));
  };

  const setHour24 = (hour: number, opts?: { advance?: boolean }) => {
    setDraft(`${pad(hour)}:${pad(draftMinute)}`);
    if (opts?.advance) setMode('minute');
  };
  const setMinute = (minute: number) => {
    setDraft(`${pad(draftHour)}:${pad(minute)}`);
  };
  const setMeridiem = (mer: Meridiem) => {
    const hour12 = draftHour % 12 === 0 ? 12 : draftHour % 12;
    const hour = mer === 'PM' ? (hour12 % 12) + 12 : hour12 % 12;
    setDraft(`${pad(hour)}:${pad(draftMinute)}`);
  };

  const hourMarks: ClockMark[] = useMemo(() => {
    if (timeFormat === '24h') {
      const outer = Array.from({ length: 12 }, (_, i) => i + 1).map((h12) => {
        const value = h12; // 1..12 as 24h hours
        return {
          key: `outer-${h12}`,
          label: pad(value),
          value,
          angleDeg: (h12 % 12) * 30,
          radiusPct: 38,
          selected: value === draftHour,
          disabled: isHourOutOfRange(value),
        };
      });
      const inner = Array.from({ length: 12 }, (_, i) => i + 1).map((h12) => {
        const value = h12 === 12 ? 0 : h12 + 12;
        return {
          key: `inner-${h12}`,
          label: pad(value),
          value,
          angleDeg: (h12 % 12) * 30,
          radiusPct: 22,
          selected: value === draftHour,
          disabled: isHourOutOfRange(value),
        };
      });
      return [...outer, ...inner];
    }
    return Array.from({ length: 12 }, (_, i) => i + 1).map((h12) => {
      const value = meridiem === 'PM' ? (h12 % 12) + 12 : h12 % 12;
      return {
        key: `hour-${h12}`,
        label: String(h12),
        value,
        angleDeg: (h12 % 12) * 30,
        radiusPct: 38,
        selected: value === draftHour,
        disabled: isHourOutOfRange(value),
      };
    });
  }, [timeFormat, draftHour, meridiem, minTime, maxTime]);

  const minuteMarks: ClockMark[] = useMemo(
    () =>
      minuteValues.map((m, i) => ({
        key: `minute-${m}`,
        label: pad(m),
        value: m,
        angleDeg: (i * 360) / minuteMarkCount,
        radiusPct: 38,
        selected: m === draftMinute,
        disabled: isMinuteOutOfRange(draftHour, m),
      })),
    [minuteValues, minuteMarkCount, draftHour, draftMinute, minTime, maxTime]
  );

  // `preview` runs continuously while dragging the clock hand — updates the value only. `commit`
  // runs once the interaction ends (release, or a plain click) and is where the hour→minute
  // auto-advance happens, so starting a drag on the hour ring doesn't instantly jump to minute mode.
  const previewMark = (v: number) => {
    if (mode === 'hour') setHour24(v);
    else setMinute(v);
  };
  const commitMark = (v: number) => {
    if (mode === 'hour') setHour24(v, { advance: true });
    else setMinute(v);
  };

  const activeMarks = mode === 'hour' ? hourMarks : minuteMarks;
  const selectedMark = activeMarks.find((m) => m.selected) ?? activeMarks[0];

  const triggerText = value ? formatTime(timeToDate(value), timeFormat) : placeholder;
  const hour12Display = draftHour % 12 === 0 ? 12 : draftHour % 12;

  // Typed-input mode: clamp on change so the draft never leaves the valid, in-range window —
  // both the format's numeric bounds and the caller's minTime/maxTime.
  const minBoundHour = minTime != null ? parseHHmm(minTime).hour : 0;
  const maxBoundHour = maxTime != null ? parseHHmm(maxTime).hour : 23;
  const clampHourToBounds = (hour: number) => clamp(hour, minBoundHour, maxBoundHour);
  const clampMinuteToBounds = (hour: number, minute: number) => {
    const lo = minTime != null && hour === minBoundHour ? parseHHmm(minTime).minute : 0;
    const hi = maxTime != null && hour === maxBoundHour ? parseHHmm(maxTime).minute : 59;
    return clamp(minute, lo, hi);
  };

  const onTypedHourChange = (raw: string) => {
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    let hour: number;
    if (timeFormat === '24h') {
      hour = clamp(n, 0, 23);
    } else {
      const h12 = clamp(n, 1, 12);
      hour = meridiem === 'PM' ? (h12 % 12) + 12 : h12 % 12;
    }
    hour = clampHourToBounds(hour);
    const minute = clampMinuteToBounds(hour, draftMinute);
    setDraft(`${pad(hour)}:${pad(minute)}`);
  };
  const onTypedMinuteChange = (raw: string) => {
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    setMinute(clampMinuteToBounds(draftHour, clamp(n, 0, 59)));
  };

  return (
    <div className={cn('flex flex-col gap-[7px]', className)}>
      {!hideTrigger && label && (
        <label htmlFor={fieldId} className="block text-[12.5px] font-semibold tracking-[0.01em] text-[var(--text-2)]">
          {label}
          <FieldLabelMark requiredMark={requiredMark} optional={optional} />
        </label>
      )}

      {!hideTrigger && <button
        id={fieldId}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerAriaLabel}
        disabled={disabled}
        onClick={openDialog}
        className={cn(
          'flex h-12 w-full items-center gap-2.5 rounded-[12px] border px-[15px] text-left',
          'transition-[background-color,border-color,box-shadow] duration-200',
          'bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[var(--text)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
          open ? 'border-[var(--primary)] bg-[var(--surface)] ring-4 ring-[var(--ring)]' : 'border-transparent',
          error && 'border-[var(--danger)]'
        )}
      >
        <svg className="h-4 w-4 shrink-0 text-[var(--text-3)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
        </svg>
        <span className={cn('min-w-0 flex-1 truncate text-[14.5px]', value ? 'text-[var(--text)]' : 'text-[var(--text-3)]')}>
          {triggerText}
        </span>
      </button>}

      {open && (
        <Modal
          title="Select time"
          onClose={cancel}
          footer={
            <div className="flex w-full items-center justify-between">
              <button
                type="button"
                aria-label={inputMode === 'clock' ? 'Switch to keyboard input' : 'Switch to clock input'}
                onClick={() => setInputMode((m) => (m === 'clock' ? 'keyboard' : 'clock'))}
                className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--text-2)] transition-colors hover:bg-[var(--surface-2)]"
              >
                {inputMode === 'clock' ? (
                  <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <path strokeLinecap="round" d="M7 9h.01M11 9h.01M15 9h.01M7 13h.01M11 13h.01M15 13h.01M7 17h6" />
                  </svg>
                ) : (
                  <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
                  </svg>
                )}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={cancel}
                  className="rounded-[9px] px-4 py-2 text-[13px] font-semibold text-[var(--text-2)] hover:bg-[var(--surface-2)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={commit}
                  className="rounded-[9px] bg-[var(--primary)] px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:opacity-90"
                >
                  OK
                </button>
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-5">
            {/* Digital display header */}
            <div className="flex items-center justify-center gap-1.5">
              <button
                type="button"
                aria-label="Select hour"
                onClick={() => {
                  setMode('hour');
                  setInputMode('clock');
                }}
                className={cn(
                  'rounded-[10px] px-3 py-1.5 text-[32px] font-bold tabular-nums transition-colors',
                  mode === 'hour' ? 'bg-[var(--primary-weak)] text-[var(--primary)]' : 'text-[var(--text)] hover:bg-[var(--surface-2)]'
                )}
              >
                {timeFormat === '24h' ? pad(draftHour) : pad(hour12Display)}
              </button>
              <span className="text-[32px] font-bold text-[var(--text-3)]">:</span>
              <button
                type="button"
                aria-label="Select minute"
                onClick={() => {
                  setMode('minute');
                  setInputMode('clock');
                }}
                className={cn(
                  'rounded-[10px] px-3 py-1.5 text-[32px] font-bold tabular-nums transition-colors',
                  mode === 'minute' ? 'bg-[var(--primary-weak)] text-[var(--primary)]' : 'text-[var(--text)] hover:bg-[var(--surface-2)]'
                )}
              >
                {pad(draftMinute)}
              </button>

              {timeFormat === '12h' && (
                <div className="ml-2 flex flex-col overflow-hidden rounded-[9px] border border-[var(--border)]">
                  <button
                    type="button"
                    aria-label="AM"
                    aria-pressed={meridiem === 'AM'}
                    disabled={isMeridiemOutOfRange('AM')}
                    onClick={() => setMeridiem('AM')}
                    className={cn(
                      'px-2.5 py-1 text-[11px] font-bold transition-colors',
                      'disabled:cursor-not-allowed disabled:opacity-40',
                      meridiem === 'AM' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-2)] hover:bg-[var(--surface-2)]'
                    )}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    aria-label="PM"
                    aria-pressed={meridiem === 'PM'}
                    disabled={isMeridiemOutOfRange('PM')}
                    onClick={() => setMeridiem('PM')}
                    className={cn(
                      'px-2.5 py-1 text-[11px] font-bold transition-colors',
                      'disabled:cursor-not-allowed disabled:opacity-40',
                      meridiem === 'PM' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-2)] hover:bg-[var(--surface-2)]'
                    )}
                  >
                    PM
                  </button>
                </div>
              )}
            </div>

            {inputMode === 'clock' ? (
              <ClockFace
                marks={activeMarks}
                handAngleDeg={selectedMark?.angleDeg ?? 0}
                handRadiusPct={selectedMark?.radiusPct ?? 38}
                ariaLabel={mode === 'hour' ? 'Hour' : 'Minute'}
                onPreview={previewMark}
                onCommit={commitMark}
              />
            ) : (
              <div className="flex items-center justify-center gap-2">
                <label className="flex flex-col items-center gap-1">
                  <span className="text-[11px] font-semibold text-[var(--text-3)]">Hour</span>
                  <input
                    aria-label="Hour"
                    type="number"
                    min={timeFormat === '24h' ? 0 : 1}
                    max={timeFormat === '24h' ? 23 : 12}
                    value={timeFormat === '24h' ? draftHour : hour12Display}
                    onChange={(e) => onTypedHourChange(e.target.value)}
                    className="h-14 w-16 rounded-[10px] border border-[var(--border)] bg-[var(--surface-2)] text-center text-[24px] font-bold tabular-nums text-[var(--text)] focus:border-[var(--primary)] focus:outline-none focus:ring-4 focus:ring-[var(--ring)]"
                  />
                </label>
                <span className="text-[24px] font-bold text-[var(--text-3)]">:</span>
                <label className="flex flex-col items-center gap-1">
                  <span className="text-[11px] font-semibold text-[var(--text-3)]">Minute</span>
                  <input
                    aria-label="Minute"
                    type="number"
                    min={0}
                    max={59}
                    value={draftMinute}
                    onChange={(e) => onTypedMinuteChange(e.target.value)}
                    className="h-14 w-16 rounded-[10px] border border-[var(--border)] bg-[var(--surface-2)] text-center text-[24px] font-bold tabular-nums text-[var(--text)] focus:border-[var(--primary)] focus:outline-none focus:ring-4 focus:ring-[var(--ring)]"
                  />
                </label>
              </div>
            )}
          </div>
        </Modal>
      )}

      {error && (
        <span className="flex items-center gap-1 text-[12px] font-medium text-[var(--danger)]">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
          </svg>
          {error}
        </span>
      )}
    </div>
  );
}
